import { createContext, Fragment, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useI18n } from '@/i18n';
import { AGENTIC_APPS, CONTACT_EMAIL } from '../data';
import en, { type LandingDict } from './en';
import zh from './zh';
import zhTW from './zh-TW';
import fr from './fr';
import de from './de';
import es from './es';
import ja from './ja';
import th from './th';
import ko from './ko';
import it from './it';
import ptBR from './pt-BR';
import nl from './nl';
import id from './id';

// Same list and order as the Estha for Mac app (frontend/src/i18n/languages.ts).
export const LANDING_LANGS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh', label: '简体中文', short: '中文' },
  { code: 'zh-TW', label: '繁體中文', short: '繁中' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'ja', label: '日本語', short: 'JA' },
  { code: 'th', label: 'ไทย', short: 'TH' },
  { code: 'ko', label: '한국어', short: 'KO' },
  { code: 'it', label: 'Italiano', short: 'IT' },
  { code: 'pt-BR', label: 'Português (Brasil)', short: 'PT' },
  { code: 'nl', label: 'Nederlands', short: 'NL' },
  { code: 'id', label: 'Bahasa Indonesia', short: 'ID' },
] as const;

export type LandingLang = (typeof LANDING_LANGS)[number]['code'];

const DICTS: Record<LandingLang, LandingDict> = {
  en, zh, 'zh-TW': zhTW, fr, de, es, ja, th, ko, it, 'pt-BR': ptBR, nl, id,
};

const STORAGE_KEY = 'estha-landing-lang';
const isLang = (v: unknown): v is LandingLang => LANDING_LANGS.some((l) => l.code === v);

const EN_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];

/** Fill {n}, {nWord} and {email} placeholders. */
export function fill(s: string): string {
  const n = AGENTIC_APPS.length;
  return s
    .split('{nWord}').join(EN_WORDS[n] ?? String(n))
    .split('{n}').join(String(n))
    .split('{email}').join(CONTACT_EMAIL);
}

/** Render a string with "\n" as line breaks (after filling placeholders). */
export function Lines({ text }: { text: string }) {
  const parts = fill(text).split('\n');
  return <>{parts.map((p, i) => <Fragment key={i}>{i > 0 && <br />}{p}</Fragment>)}</>;
}

interface Ctx { lang: LandingLang; setLang: (l: LandingLang) => void; t: LandingDict }
const LandingLangContext = createContext<Ctx | null>(null);

export function LandingLangProvider({ children }: { children: ReactNode }) {
  const { language, setLanguage } = useI18n();
  // Default is English. Only an explicit choice (stored) or the site-wide 中文 toggle changes it.
  const [lang, setLangState] = useState<LandingLang>(() => {
    // A shared link can carry the sender's language (?lang=ja); that counts as an explicit choice.
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('lang');
      if (isLang(fromUrl)) return fromUrl;
    } catch { /* no URL */ }
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (isLang(saved)) return saved;
    } catch { /* storage unavailable */ }
    return language === 'zh' ? 'zh' : 'en';
  });
  const setLang = useCallback((l: LandingLang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* storage unavailable */ }
    // Keep the site-wide en/zh setting (download buttons, MyOrg/admin pages) in step.
    setLanguage(l === 'zh' ? 'zh' : 'en');
  }, [setLanguage]);
  const value = useMemo(() => ({ lang, setLang, t: DICTS[lang] ?? en }), [lang, setLang]);
  return <LandingLangContext.Provider value={value}>{children}</LandingLangContext.Provider>;
}

export function useLanding() {
  const ctx = useContext(LandingLangContext);
  if (!ctx) throw new Error('useLanding must be used within LandingLangProvider');
  return ctx;
}
